import React from 'react';
import { useHistory } from 'react-router-dom';
import {
    IonItem,
    IonLabel,
    IonInput,
    IonButton,
    IonText,
    useIonToast,
    IonSpinner
} from '@ionic/react';
import { useAuth } from '../contexts/AuthContext';
import { PasswordRequirements } from './PasswordRequirements';
import { PasswordMatchIndicator } from './PasswordMatchIndicator';

export const RegisterForm: React.FC = () => {
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);
    const [present] = useIonToast();
    const { register } = useAuth();
    const [isLoading, setIsLoading] = React.useState(false);
    const history = useHistory();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Basic validation
        if (!email || !password || !confirmPassword) {
            setError('All fields are required');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await register({ emailAddress: email, password });
            present({
                message: 'Registration successful! Please log in.',
                duration: 3000,
                position: 'bottom',
                color: 'success'
            });
            history.push('/login');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Registration failed';
            setError(message);
            present({
                message,
                duration: 3000,
                position: 'bottom',
                color: 'danger'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <IonItem>
                <IonLabel position="floating">Email</IonLabel>
                <IonInput
                    type="email"
                    value={email}
                    onInput={e => setEmail((e.target as HTMLInputElement).value)}
                    required
                    disabled={isLoading}
                />
            </IonItem>

            <IonItem>
                <IonLabel position="floating">Password</IonLabel>
                <IonInput
                    type="password"
                    value={password}
                    onInput={e => setPassword((e.target as HTMLInputElement).value)}
                    required
                    disabled={isLoading}
                />
            </IonItem>

            <PasswordRequirements password={password} />

            <IonItem>
                <IonLabel position="floating">Confirm Password</IonLabel>
                <IonInput
                    type="password"
                    value={confirmPassword}
                    onInput={e => setConfirmPassword((e.target as HTMLInputElement).value)}
                    required
                    disabled={isLoading}
                />
            </IonItem>

            <PasswordMatchIndicator password={password} confirmPassword={confirmPassword} />

            {error && (
                <IonText color="danger" className="ion-padding">
                    <p>{error}</p>
                </IonText>
            )}

            <div className="ion-padding">
                <IonButton
                    expand="block"
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? <IonSpinner name="dots" /> : 'Register'}
                </IonButton>
                <IonButton
                    expand="block"
                    fill="clear"
                    routerLink="/login"
                    disabled={isLoading}
                >
                    Already have an account? Log in
                </IonButton>
            </div>
        </form>
    );
}; 